import { useId, useRef, type ReactNode } from "react";
import { cn } from "@/lib/cn";

export interface TabItem {
  value: string;
  label: ReactNode;
  /** Optional trailing element (e.g. a lock icon for the money-gated Invoice tab). */
  adornment?: ReactNode;
  disabled?: boolean;
}

export interface TabsProps {
  items: TabItem[];
  value: string;
  onValueChange: (value: string) => void;
  /** Accessible name for the tablist. */
  label: string;
  className?: string;
}

/**
 * Accessible tab strip (APG roving tabindex: Arrow keys move BOTH selection and focus, Home/End jump to
 * the first/last enabled tab). Underline-led active state, not a heavy pill — keeps a data UI calm.
 */
export function Tabs({ items, value, onValueChange, label, className }: TabsProps) {
  const baseId = useId();
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  // Focus MUST follow selection — otherwise the roving tabindex strands focus on a now-untabbable tab and
  // the far tabs become unreachable by keyboard.
  function move(toValue: string) {
    onValueChange(toValue);
    // Uncancelled on purpose, and audited (2026-09-18, with the setTimeout sweep): this fires from a key
    // handler, not an effect, so there is no cleanup to hang it on — and it needs none. The callback only
    // reads a ref and calls focus(): no state write, no `window` access, so a frame landing after unmount
    // is a no-op on a detached node, not the teardown crash that audit was chasing.
    requestAnimationFrame(() => tabRefs.current[toValue]?.focus());
  }

  function onKeyDown(e: React.KeyboardEvent) {
    const enabled = items.filter((i) => !i.disabled);
    if (enabled.length === 0) return;
    const cur = Math.max(
      0,
      enabled.findIndex((i) => i.value === value),
    );
    let nextIdx: number;
    if (e.key === "ArrowRight") nextIdx = (cur + 1) % enabled.length;
    else if (e.key === "ArrowLeft") nextIdx = (cur - 1 + enabled.length) % enabled.length;
    else if (e.key === "Home") nextIdx = 0;
    else if (e.key === "End") nextIdx = enabled.length - 1;
    else return;
    e.preventDefault();
    // nextIdx is always in range (modulo over a non-empty `enabled`), but the index access is unchecked —
    // narrow instead of asserting so a future caller passing 0 tabs can never turn this into a TypeError.
    const next = enabled[nextIdx];
    if (next) move(next.value);
  }

  return (
    <div
      role="tablist"
      aria-label={label}
      className={cn("flex items-center gap-1 border-b border-(--border)", className)}
    >
      {items.map((item) => {
        const active = item.value === value;
        return (
          <button
            key={item.value}
            ref={(el) => {
              tabRefs.current[item.value] = el;
            }}
            role="tab"
            id={`${baseId}-tab-${item.value}`}
            aria-selected={active}
            aria-controls={`${baseId}-panel-${item.value}`}
            tabIndex={active ? 0 : -1}
            disabled={item.disabled}
            onClick={() => onValueChange(item.value)}
            onKeyDown={onKeyDown}
            className={cn(
              "relative inline-flex items-center gap-1.5 rounded-t-(--radius-sm) px-3.5 py-2.5 text-(length:--text-sm) font-medium " +
                "transition-colors duration-(--dur-fast) ease-(--ease-out) " +
                "focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-(--brand) " +
                "disabled:opacity-40 disabled:pointer-events-none",
              active
                ? "text-(--brand-strong) after:absolute after:inset-x-3 after:-bottom-px after:h-[2px] after:rounded-full after:bg-(--brand)"
                : "text-(--text-muted) hover:text-(--text)",
            )}
          >
            {item.label}
            {item.adornment}
          </button>
        );
      })}
    </div>
  );
}

export interface TabPanelProps {
  id: string;
  value: string;
  active: boolean;
  children: ReactNode;
  className?: string;
}

export function TabPanel({ id, value, active, children, className }: TabPanelProps) {
  if (!active) return null;
  return (
    <div
      role="tabpanel"
      id={`${id}-panel-${value}`}
      aria-labelledby={`${id}-tab-${value}`}
      tabIndex={0}
      className={cn(
        "focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-(--brand)",
        className,
      )}
    >
      {children}
    </div>
  );
}
